//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

/* global angular: false */
/* eslint no-underscore-dangle: ["error", { "allow": ["_scope_id"] }] */

import _ from "js/underscore-mixins";

(function() {
  const DEFAULT_MIN_SEARCH_LENGTH = 5;
  const PRE_DATA_SET_KEY = 'pre';

    // Keys with specific typeahead functionality attached
  const keyCodeMap = {
    9: 'tab',
    27: 'esc',
    37: 'left',
    39: 'right',
    13: 'enter',
    38: 'up',
    40: 'down',
  };

    // Functions triggered by typeahead with the 'typeahead:' prefix
  const typeaheadEvents = [
    'cursorchanged',
    'opened',
    'closed',
    'autocompleted',
    'selected',
  ];

    // Functions triggered by the typeahead input with the '.tt' suffix
  const ttInputEvents = [
    'blur',
    'focus',
    'keydown',
  ];

    /**
     * twitterMultiTypeahead
     *
     * Wrapper directive around Twitter's jQuery typeahead plugin with support for multiple datasets.
     *
     * Scope:
     *  setKeys {Array}:
     *      A set of keys defining each dataset. These keys will be used to access relevant templates, getData functions, result data, etc.
     *  getData {Function}:
     *      An single Angular expression which returns a Promise which resolves to an array of 'results'.
     *      Note: In the context of a multiset typeahead, this function can be used to populate multiple data sets.
     *      In this case, the Promise result should be an Object of the form { setKey[0]: resultArr2, setKey[1]: resultsArr1, ... }
     *  getDataFns {Object[Function]}:
     *      A set of getData functions each of which returns a Promise which resolves to an array of 'results'.
     *      Note: If a specific getData function exists for a setKey, the results of that function will override the results of getData.
     *  templateSet {Object[Object]}:
     *      An set of Objects which contain information for how to retrieve/render templates.
     *      Each template object should use a valid twitter typeahead template-name as the key ['header', 'footer', 'empty', 'suggestion'].
     *      Each template-name should point to another object which contains the template information. For instance:
     *      { 'url': '/path/to/html/template' } or { 'html' : '<div>{{ arbitrary Angular expression }}</div>' }
     *  key {String || Function}:
     *      Describes the value of a particular expression.
     *      See: https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#datasets
     *  context {Object}:
     *      An object containing values that will be accessible in the scope in which the 'suggestion' template is rendered
     *      This is used to pass arbitrary values or out-of-scope functions into the template's isolate scope
     *      A particular example would be to pass a function used as an ng-click callback. See locations.js for an example of this
     */
  angular.module('curalate').directive('twitterMultiTypeahead', [
    '$rootScope',
    '$compile',
    '$interpolate',
    '$timeout',
    '$http',
    '$q',
    'twitterTypeahead.templateDefaults',
    'TrackingService',
    function(
            $rootScope,
            $compile,
            $interpolate,
            $timeout,
            $http,
            $q,
            templateDefaults,
            TrackingService,
        ) {
      return {
        restrict: 'AC',
        transclude: true,
        scope: {
          setKeys: '=',
          getData: '=',
          getDataFns: '=',
          templateSets: '=',
          key: '=',
          events: '=?',
          context: '=',
          trackCategory: '@',
          fixedScrollBind: '@?',
          autoFocus: '<?',
        },
        link(scope, element, attrs) {
          const minLength = (isNaN(attrs.minLength)) ? DEFAULT_MIN_SEARCH_LENGTH : parseInt(attrs.minLength);

                    // We define a special setKey which is used with the dataset we generate for a single `getData` function
          const allSetKeys = scope.setKeys.concat([PRE_DATA_SET_KEY]);
          const bindTypeahead = function(templateSets) {
            const compiledTemplates = {};
            const linkedTemplates = {};
            const rendered = {};
            let resultTotal = 0;
            let lastQuery;
            let lastCursorItem;
            let deferred;

            const generateHtmlFunc = function(where) {
              const ele = angular.element(where);

                            // Return DOM if we have it in order to preserve events
              return ele.length > 0 ? ele[0] : '';
            };

                        // Combine default template set with the template sets passed into the function.
                        // Compile all of them and return an map where template is stored at `templates[setKey][templateName]`
            _(allSetKeys).each((setKey, i) => {
              const templates = templateSets[i];

              compiledTemplates[setKey] = _.chain(templateDefaults)
                                .map((value, key) => {
                                  const obj = {};
                                  const existing = _(templates).findWhere({ type: key });
                                  obj[key] = (existing && existing.template) ? $compile(existing.template) : $compile(value);
                                  return obj;
                                })
                                .reduce((memo, value) => _.extend(memo, value), {})
                                .value();
            });

            function generateTypeaheadDatasets() {
              const datasets = [];
                            // If a single `getData` function was defined, set up initial placeholder dataset to populate data.
                            // On update, twitter typeahead fetches data for each dataset IN ORDER, so we use this placeholder
                            // to fetch communal data before updates are made to subsequent datasets.
              if (scope.getData) {
                datasets.push({
                  source(query, cb, asynccb) {
                    lastQuery = query;

                    deferred = $q.defer();
                    scope.getData(query, deferred);
                    deferred.promise.then((rawResults) => {
                      const setKey = PRE_DATA_SET_KEY;
                      const allResults = _(rawResults).chain().values().flatten().value();

                      const preScope = scope.$new(true);
                      preScope.query = query;
                      preScope.isEmpty = allResults.length === 0;
                      preScope.resultTotal = allResults.length;

                                            // Re-link relevant template with the new state
                      linkedTemplates[setKey] = {};
                      linkedTemplates[setKey].empty = compiledTemplates[setKey].empty(preScope, angular.noop);
                      linkedTemplates[setKey].header = compiledTemplates[setKey].header(preScope, angular.noop);

                                            // Only hit TrackingService if the custom getData function resolved the deferred we passed
                      TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadSearch', query);
                      if (preScope.isEmpty) {
                        TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadSearch', `${query}:empty`);
                      }

                                            // Wait until the current $digest loop finishes to call the typeahead callback
                                            // so the data bindings for the templates have a chance to update
                      $timeout(() => {
                        if (_.isFunction(asynccb)) {
                          asynccb(rawResults);
                        } else {
                          cb(rawResults);
                        }
                      });
                    });
                  },
                  templates: {
                    header() {
                      return generateHtmlFunc(linkedTemplates[PRE_DATA_SET_KEY].header);
                    },
                    empty() {
                      return generateHtmlFunc(linkedTemplates[PRE_DATA_SET_KEY].empty);
                    },
                  },
                });
              }

                            // For each input setKey, generate an a corresponding dataset.
                            // Each setKey is used to link associated getDataFns, templates, etc.
              _(scope.setKeys).each((setKey, keyIndex) => {
                datasets.push({
                  source(query, cb, asynccb) {
                    let dataSetDeferred;
                    const isFirstSet = (keyIndex === 0);
                    const isLastSet = (keyIndex - 1 === scope.setKeys.length);
                    if (isFirstSet) {
                      lastCursorItem = null;
                    }
                    lastQuery = query;

                                        // If a specific getDataFn was defined for this dataset,
                                        // use that call instead of the placeholder.
                    if (scope.getDataFns && scope.getDataFns[setKey]) {
                      dataSetDeferred = $q.defer();
                      scope.getDataFns[setKey](query, dataSetDeferred);
                    }

                    (dataSetDeferred ? dataSetDeferred.promise : deferred.promise).then((rawResults) => {
                                            // If we are relying on a specific getDataFn, use the raw results
                                            // otherwise look for the set data in the grouped getData results
                      const results = (dataSetDeferred ? rawResults : rawResults[setKey]) || [];
                      const prevResults = isFirstSet ? null : rawResults[scope.setKeys[keyIndex - 1]];
                      const nextResults = isLastSet ? null : rawResults[scope.setKeys[keyIndex + 1]];

                                            // Since the first item is displayed as a suggestion, we set it as the last "selected" item
                      if (!lastCursorItem) {
                        lastCursorItem = results[0];
                      }

                                            // Keep a running total of results
                      if (isFirstSet) {
                        resultTotal = results.length;
                      } else {
                        resultTotal += results.length;
                      }

                                            // Once the last result set is loaded, if there are no results, clear out the last cursor item
                      if (isLastSet && resultTotal === 0) {
                        lastCursorItem = null;
                      }

                                            // Only hit TrackingService if the custom getData function resolved the deferred we passed
                      if (dataSetDeferred) {
                        TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadSearch', `${setKey}:${query}`);
                      }

                                            // Update scope variables with current state so templates have access to them
                      const dataSetScope = scope.$new(true);
                      dataSetScope.context = scope.context;
                      dataSetScope.setKey = setKey;
                      dataSetScope.query = query;
                      dataSetScope.isEmpty = results.length === 0;
                      dataSetScope.prevEmpty = !prevResults || !prevResults.length;
                      dataSetScope.nextEmpty = !nextResults || !nextResults.length;
                      dataSetScope.resultTotal = results.length;

                                            // Re-link the three 'static' templates with the new state
                      linkedTemplates[setKey] = {};
                      linkedTemplates[setKey].header = compiledTemplates[setKey].header(dataSetScope, angular.noop);
                      linkedTemplates[setKey].footer = compiledTemplates[setKey].footer(dataSetScope, angular.noop);
                      linkedTemplates[setKey].empty = compiledTemplates[setKey].empty(dataSetScope, angular.noop);

                                            // Destroy old scopes from previously rendered items
                      _.chain(rendered[setKey])
                                                .map(dom => angular.element(dom).scope())
                                                .compact()
                                                .each((renderedScope) => { renderedScope.$destroy(); });

                      rendered[setKey] = {};

                                            // Link each result against the template
                      if (results.length > 0) {
                        _(results).each((result, i) => {
                          const newScope = scope.$new(true);
                          result._scope_id = newScope.$id;
                          newScope.data = result;
                          newScope.data.index = i;
                          newScope.context = scope.context;
                          newScope.isEmpty = dataSetScope.isEmpty;
                          newScope.setKey = setKey;
                          newScope.query = query;

                                                    // Pass the noop function to get element cloning only
                          rendered[setKey][result._scope_id] = compiledTemplates[setKey].suggestion(newScope, angular.noop);
                        });
                      }

                                            // Wait until the current $digest loop finishes to call the typeahead callback
                                            // so the data bindings for the templates have a chance to update
                      $timeout(() => {
                        if (_.isFunction(asynccb)) {
                          asynccb(results);
                        } else {
                          cb(results);
                        }
                      });
                    });
                  },
                  templates: {
                    header() {
                      return generateHtmlFunc(linkedTemplates[setKey].header);
                    },
                    footer() {
                      return generateHtmlFunc(linkedTemplates[setKey].footer);
                    },
                    empty() {
                      return generateHtmlFunc(linkedTemplates[setKey].empty);
                    },
                    suggestion(obj) {
                      return generateHtmlFunc(rendered[setKey][obj._scope_id]);
                    },
                  },
                });
              });

              return datasets;
            }

                        // Build the typeahead element
            const typeaheadElem = angular.element(element).typeahead({
              minLength,
              highlight: true,
              displayKey(suggestion) {
                let fn;
                if (angular.isFunction(scope.key)) {
                  fn = scope.key.call(this, suggestion);
                } else {
                  fn = suggestion[scope.key || 'value'];
                }
                return fn;
              },
            }, generateTypeaheadDatasets());

                        // Tie into events triggered within the typeahead library
            scope.events = scope.events || {};

                        // 1) Tie into typeahead custom events
            _.chain(scope.events).keys().intersection(typeaheadEvents).each((key) => {
              typeaheadElem.on(`typeahead:${key}`, (...args) => {
                scope.$apply(() => {
                  scope.events[key].apply(typeaheadElem, args);
                  TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadInteraction', key);
                });
              });
            });

                        // Keep track to item cursor points to in order to enrich DOM events below
                        // NOTE: cursor change events are only fired for keyboard navigation
            typeaheadElem.on('typeahead:cursorchanged', (e, rawSelection) => {
              TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadInteraction', 'cursorchanged');
              lastCursorItem = rawSelection;
            });

                        // 2) Tie into typeahead input DOM events
            _.chain(scope.events).keys().intersection(ttInputEvents).each((key) => {
              typeaheadElem.on(`${key}.tt`, (...args) => {
                scope.$apply(() => {
                  scope.events[key].apply(typeaheadElem, args);
                  TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadInteraction', key);
                });
              });
            });

                        // Keydown events are named slightly differently
            typeaheadElem.on('keydown.tt', (e) => {
              const key = keyCodeMap[e.which || e.keyCode];

              if (key && scope.events[`${key}Keyed`]) {
                scope.$apply(() => {
                  scope.events[`${key}Keyed`].call(typeaheadElem, e, lastCursorItem, lastQuery);
                  TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadInteraction', key);
                });
              }
            });

                        // Add watcher events to trigger specific typeahead behavior
            scope.$on('twitter-typeahead:submit', (e, key) => {
              scope.events[`${key}Keyed`].call(typeaheadElem, e, lastCursorItem, lastQuery);
              TrackingService.recordEvent(scope.trackCategory, 'MultiTypeaheadInteraction', 'submit');
            });

            scope.$on('twitter-typeahead:reset', () => {
              const $elem = angular.element(element);
              lastCursorItem = null;
              resultTotal = 0;
              $elem.typeahead('val', null);
              $elem.typeahead('close');
              $elem.blur();
            });

                        /**
                         * This option enables a hack to position the typeahead dropdown as 'fixed' instead of absolute.
                         * We use this on Influencers Sidebar to keep the typeahead dropdown from
                         * being clipped by the 'overflow-y: scroll' container it lives within.
                         */
            if (scope.fixedScrollBind) {
                            // Element to position the dropdown based on--this is the typeahead input box.
              const offsetSource = typeaheadElem.parent();
                            // Element that needs to be positioned (the results dropdown)
              const positionTarget = offsetSource.find('.tt-dropdown-menu');

                            // Method to update the position of the dropdown based on the screen position of the input box
              const updateFixedPosition = function() {
                                // getBoudingClientRect gives us positions relative to the current viewport,
                                // so we can use it to set fixed positions
                const rect = offsetSource[0].getBoundingClientRect();

                                // Top of the dropdown should be just past the input box--so top + height.
                positionTarget[0].style.top = `${rect.top + rect.height}px`;
                positionTarget[0].style.left = `${rect.left}px`;
              };

              const registerListeners = function() {
                updateFixedPosition();
                angular.element(scope.fixedScrollBind).on('scroll', updateFixedPosition);
                angular.element(window).on('resize', updateFixedPosition);
              };
              const unregisterListeners = function() {
                angular.element(scope.fixedScrollBind).off('scroll', updateFixedPosition);
                angular.element(window).off('resize', updateFixedPosition);
              };

                            // Set the dropdown to use fixed positioning.
                            // Typeahead will override this if we try to set it with just a CSS class, so we use inline.
              positionTarget[0].style.position = 'fixed';

                            // Register listeners to start tracking scrolls and update positioning of the dropdown
                            // as the dropdown opens/closes.
              typeaheadElem.on('typeahead:opened', registerListeners);
              typeaheadElem.on('typeahead:closed', unregisterListeners);

              scope.$on('$destroy', () => {
                unregisterListeners();
                typeaheadElem.off('typeahead:opened', registerListeners);
                typeaheadElem.off('typeahead:closed', unregisterListeners);
              });

                            // For good measure, in case the dropdown is somehow already opened.
              updateFixedPosition();
            }

            if (scope.autoFocus) {
              $timeout(() => {
                typeaheadElem.focus();
              });
            }
          };

                    // Create a list of promises, one for each template set.
                    // If the template is just a string, create a pre-resolved promise.
                    // If the template is a url, request it and return the promise.
          const promises = _(allSetKeys).map((setKey) => {
            const templates = scope.templateSets[setKey];

            const templatePromises = _(templates).map((obj, key) => {
              let deferred;
              let promise;

              if (obj.url) {
                deferred = $q.defer();
                $http.get(obj.url).then((result) => {
                  deferred.resolve({
                    type: key,
                    template: result.data,
                  });
                });
                promise = deferred.promise;
              } else {
                promise = $q.when({
                  type: key,
                  template: obj.html,
                });
              }
              return promise;
            });

            return $q.all(templatePromises);
          });

                    // Combine the list of promises in a single promise and
                    // bind the typeahead only when all promises have returned.
          $q.all(promises).then(bindTypeahead);
        },
      };
    },
  ]);
})();
